import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingDown, Users, Sparkles, CreditCard, Brain, CalendarDays, Loader2, Key, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUNNEL_COLORS = [
  { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-700', bar: '#78716c' },
  { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', bar: '#FF6B6B' },
  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: '#f59e0b' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: '#10b981' },
  { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', bar: '#0ea5e9' },
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', bar: '#8b5cf6' },
];

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Tout' },
  { value: '30', label: '30j' },
  { value: '7', label: '7j' },
];

const AdminFunnel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/funnel`, { params: { days: period === 'all' ? 0 : parseInt(period) } });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Accès réservé à l'administrateur");
        navigate('/dashboard');
      }
      console.warn('Funnel:', e.message);
    } finally { setLoading(false); }
  }, [navigate, period]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  const steps = data?.steps || [];
  const maxValue = steps.length > 0 ? steps[0].count : 1;

  const getConversionRate = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current / previous) * 100).toFixed(1);
  };

  const stepIcons = [
    <Users className="w-4 h-4" />,
    <Sparkles className="w-4 h-4" />,
    <Brain className="w-4 h-4" />,
    <CreditCard className="w-4 h-4" />,
    <Brain className="w-4 h-4" />,
    <CalendarDays className="w-4 h-4" />,
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-stone-600 hover:text-stone-900" data-testid="funnel-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-[#FF6B6B]" />
            <span className="font-serif font-semibold text-stone-800">Funnel de conversion</span>
          </div>
          <button onClick={() => navigate('/admin/users')} className="text-stone-400 hover:text-stone-600" data-testid="go-users-link">
            <Users className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Period selector */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-stone-500">
            {period === 'all' ? 'Toutes les données' : `${period} derniers jours`}
            {data?.period_start && period !== 'all' && (
              <span className="text-stone-400 ml-1">
                (depuis {new Date(data.period_start).toLocaleDateString('fr-FR')})
              </span>
            )}
          </p>
          <div className="flex bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="period-selector">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  period === opt.value
                    ? 'bg-stone-900 text-white'
                    : 'text-stone-500 hover:bg-stone-50'
                }`}
                data-testid={`period-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Global conversion */}
        {steps.length >= 2 && (
          <div className="bg-white rounded-2xl p-5 border border-stone-100 mb-6" data-testid="global-conversion">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">Conversion globale</p>
                <p className="text-3xl font-serif font-bold text-stone-900">
                  {steps[steps.length - 1].count > 0 && steps[0].count > 0
                    ? `${((steps[steps.length - 1].count / steps[0].count) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  {steps[0].label} → {steps[steps.length - 1].label}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-stone-700">{steps[0].count} inscrits</p>
                <ChevronRight className="w-4 h-4 text-stone-300 inline" />
                <p className="text-sm font-medium text-emerald-600">{steps[steps.length - 1].count} payants</p>
              </div>
            </div>
          </div>
        )}

        {/* Funnel steps */}
        <div className="space-y-3" data-testid="funnel-steps">
          {steps.map((step, idx) => {
            const widthPct = maxValue > 0 ? Math.max((step.count / maxValue) * 100, 8) : 8;
            const convRate = idx > 0 ? getConversionRate(step.count, steps[idx - 1].count) : null;
            const colors = FUNNEL_COLORS[idx % FUNNEL_COLORS.length];
            const dropOff = idx > 0 ? steps[idx - 1].count - step.count : 0;

            return (
              <div key={step.key}>
                {/* Conversion arrow between steps */}
                {idx > 0 && (
                  <div className="flex items-center justify-center gap-2 py-1.5" data-testid={`conversion-${step.key}`}>
                    <div className="h-px flex-1 bg-stone-100" />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      parseFloat(convRate) >= 50 ? 'bg-emerald-50 text-emerald-600' :
                      parseFloat(convRate) >= 25 ? 'bg-amber-50 text-amber-600' :
                      'bg-rose-50 text-rose-600'
                    }`}>
                      {convRate}% conversion
                    </span>
                    {dropOff > 0 && (
                      <span className="text-[10px] text-stone-300">
                        -{dropOff} perdus
                      </span>
                    )}
                    <div className="h-px flex-1 bg-stone-100" />
                  </div>
                )}

                {/* Step card */}
                <div className={`${colors.bg} rounded-xl p-4 border ${colors.border} relative overflow-hidden`} data-testid={`step-${step.key}`}>
                  {/* Background bar */}
                  <div
                    className="absolute inset-y-0 left-0 opacity-10 rounded-xl"
                    style={{ width: `${widthPct}%`, backgroundColor: colors.bar }}
                  />

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.text}`} style={{ backgroundColor: `${colors.bar}15` }}>
                        {stepIcons[idx] || <Users className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${colors.text}`}>{step.label}</p>
                        <p className="text-[10px] text-stone-400">{step.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-serif font-bold ${colors.text}`}>{step.count}</p>
                      {idx > 0 && (
                        <p className="text-[10px] text-stone-400">
                          {((step.count / maxValue) * 100).toFixed(0)}% du total
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick links */}
        <div className="mt-8 flex gap-3">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex-1 bg-white rounded-xl p-3 border border-stone-100 text-sm text-stone-600 hover:bg-stone-50 transition-colors text-center"
            data-testid="link-admin-users"
          >
            <Users className="w-4 h-4 mx-auto mb-1 text-stone-400" />
            Suivi utilisateurs
          </button>
          <button
            onClick={() => navigate('/admin/promo')}
            className="flex-1 bg-white rounded-xl p-3 border border-stone-100 text-sm text-stone-600 hover:bg-stone-50 transition-colors text-center"
            data-testid="link-admin-promo"
          >
            <Key className="w-4 h-4 mx-auto mb-1 text-stone-400" />
            Codes promo
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminFunnel;
